import { useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { CustomFormData } from 'shared/types/account/Account';
import { useAccount, useCart } from '@/frontastic';
import { getCookieValue, TranslateText } from '@/helpers/utils/siteUtils';
import {
  AddressPayload,
  CustomerAddressPayload,
  UpdateOrderPayload,
} from '@/sdk/composable-commerce/types/payloads/AccountPayloads';
import { COOKIE_KEYS, DEFAULT_SITE_LANG, MAX_PAGE_SIZE } from '../../../../../../constants/constant';
import { DEFAULT_COUNTRY, PAYLOAD_QUERY, RESPONSE_SUCCESS_MESSAGE } from '../../../customer-detail/constants';
import { ShippingAddressDetailsType } from '../../../customer-detail/types';
import { transformShippingAddress } from '../../../customer-detail/utils';
import ConfirmEditAddressModal from '../../../modals/orderDetails/confirmEditAddress';
import ShippingEditAddressModal from '../../../modals/orderDetails/editAddressModal';
import EditAddress from '../../../modals/orderDetails/editOrderAddress';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import Loader from '../../../ui/loader';
import { maskPhoneNumber, maskPostalCode } from '../../../utils/formatUtils';
import { PageTexts } from '../../i18n';
import { Address, ShippingAndBillingDetailsType } from '../../types';

const OrderShipmentDetails = ({
  shippingDetails,
  RecipientAddresses,
  customerId,
  isAddressAdjustable,
  isOrderAddressEditable,
  giftMessage,
  showActiveBrandSection = true,
}: {
  shippingDetails: ShippingAndBillingDetailsType;
  RecipientAddresses: Address;
  customerId: string;
  isAddressAdjustable: boolean;
  isOrderAddressEditable: boolean;
  giftMessage?: string;
  showActiveBrandSection?: boolean;
}) => {
  const convertShippingAddressToEditAddressData = () => {
    return {
      fullName: RecipientAddresses?.PersonInfo?.firstName + ' ' + RecipientAddresses?.PersonInfo?.lastName,
      firstName: RecipientAddresses?.PersonInfo?.firstName,
      addressId: RecipientAddresses?.HeaderInfo?.addressId,
      lastName: RecipientAddresses?.PersonInfo?.lastName,
      streetName: RecipientAddresses?.LocationInfo?.line1,
      city: RecipientAddresses?.LocationInfo?.city,
      country: RecipientAddresses?.LocationInfo?.country,
      state: RecipientAddresses?.LocationInfo?.state,
      postalCode: RecipientAddresses?.LocationInfo?.zip,
      phone: RecipientAddresses?.LocationInfo?.phoneNumber,
      additionalAddressInfo: RecipientAddresses?.LocationInfo?.line2,
      companyName: RecipientAddresses?.PersonInfo?.companyName,
    };
  };
  const { data: cartData } = useCart(true);
  const params = useSearchParams();
  const Texts = TranslateText(PageTexts, DEFAULT_SITE_LANG);
  const [editAddressOpen, setEditAddressOpen] = useState(false);
  const [confirmEditOpen, setConfirmEditOpen] = useState(false);
  const [updatedAddressPayload, setUpdatedAddressPayload] = useState({} as AddressPayload);
  const [shippingAddress, setShippingAddress] = useState<any>(convertShippingAddressToEditAddressData());
  const [selectedAddress, setSelectedAddress] = useState(shippingAddress);
  const [updatedShippingAddress, setUpdatedShippingAddress] = useState({} as CustomFormData);
  const { getCustomerAddresses, updateOrder, updateCustomerAddress } = useAccount();
  const [addresses, setAddresses] = useState<ShippingAddressDetailsType[]>([]);
  const [loading, setLoading] = useState(false);
  const [openEditAddressModal, setOpenEditAddressModal] = useState(false);

  const handleOrderShippingAddressUpdated = (address: CustomFormData) => {
    setShippingAddress(address);
    setSelectedAddress(address);
    setEditAddressOpen(false);
  };

  const getUpdatedAddress = (addressId: string, data: CustomFormData) => {
    setUpdatedShippingAddress({ ...data, addressId });
    setSelectedAddress({ ...data, addressId });
  };
  const handleAddressChange = (addressId: string) => {
    const selectedAddress = addresses?.find((Address: CustomFormData) => Address?.addressId === addressId);
    setSelectedAddress(selectedAddress);
  };

  const getAddressUpdateStatus = (status: boolean) => {
    if (status) {
      setShippingAddress(updatedShippingAddress);
    }
  };

  const getShippingAddress = async (makeAPIcall?: boolean) => {
    setLoading(true);
    const payload = {
      query: {
        omsCustomerId: customerId,
        pageSize: MAX_PAGE_SIZE,
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
    } as CustomerAddressPayload;
    await getCustomerAddresses(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        const transformedAddress = transformShippingAddress(response?.RecipientAddresses || []);
        const isBillingAddress =
          transformedAddress?.find((address) => address?.addressId === shippingAddress?.addressId) ?? {};
        if (Object.keys(isBillingAddress)?.length === 0) {
          setAddresses([selectedAddress, ...transformedAddress]);
        } else {
          setAddresses([...transformedAddress]);
        }

        if (makeAPIcall) {
          onAddressChange(transformedAddress?.[transformedAddress?.length - 1]?.addressId, transformedAddress);
        }
      }
    });
    setLoading(false);
  };

  const handleSubmitAddress = async () => {
    await getShippingAddress(true);
  };

  const handleChangeAddress = () => {
    getShippingAddress();
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEditAddress = (addressId: string, data: CustomFormData) => {
    setUpdatedShippingAddress({ ...data, addressId });
    const editedAddressPayload = getPayload(data, addressId);
    setUpdatedAddressPayload(editedAddressPayload);
    setConfirmEditOpen(true);
  };

  const onAddressChange = async (addressId: string, transformedAddress?: CustomFormData[]) => {
    setLoading(true);
    const payload = {
      query: {
        orderId: params.get(PAYLOAD_QUERY.ORDER_ID),
        divisionCode: params.get(PAYLOAD_QUERY.DIVISION_CODE),
      },
      data: {
        Recipients: [
          {
            Address: {
              HeaderInfo: {
                addressId: addressId,
              },
            },
          },
        ],
      },
    } as UpdateOrderPayload;
    await updateOrder(payload).then((res) => {
      if (res && res?.code === RESPONSE_SUCCESS_MESSAGE) {
        setEditAddressOpen(false);
        if (transformedAddress) {
          const updatedAddress = transformedAddress?.filter(
            (address: CustomFormData) => address?.addressId === addressId,
          );
          setShippingAddress(updatedAddress[0]);
          setSelectedAddress(updatedAddress[0]);
        } else {
          setShippingAddress(selectedAddress);
        }
      }
    });
    setLoading(false);
  };

  const handleSubmitEditAddress = async () => {
    setLoading(true);
    const oldAddressId = RecipientAddresses?.HeaderInfo?.addressId;
    const newAddressId = selectedAddress?.addressId;
    if (oldAddressId === newAddressId) {
      await updateExistingAddress(oldAddressId);
    } else if (oldAddressId !== newAddressId) {
      await updateExistingAddress(newAddressId);
      await onAddressChange(newAddressId);
    }
    setLoading(false);
  };

  const updateExistingAddress = async (addressId: string) => {
    const payload = getPayload(selectedAddress, addressId);
    await updateCustomerAddress(payload).then((response) => {
      if (response?.code === RESPONSE_SUCCESS_MESSAGE) {
        setEditAddressOpen(false);
        setShippingAddress(selectedAddress);
      }
    });
  };

  const getPayload = (address: CustomFormData, addressId: string) => {
    const payload = {
      query: {
        omsCustomerId: customerId,
        addressId: addressId,
        divisionCode: getCookieValue(COOKIE_KEYS?.selectedSite)?.slice(0, 2) ?? '',
      },
      data: {
        LocationInfo: {
          isResidential: true,
          line1: address?.streetName,
          line2: address?.additionalAddressInfo,
          city: address?.city,
          state: address?.state,
          zip: address?.postalCode,
          country: DEFAULT_COUNTRY,
          phoneNumber: address?.phone,
        },
        PersonInfo: {
          firstName: address?.firstName,
          lastName: address?.lastName,
          companyName: address?.companyName ?? '',
        },
      },
    } as AddressPayload;
    return payload;
  };

  return (
    <Card className="mb-5 w-full rounded-md border-foggyGray shadow-lg md:mb-0">
      <CardHeader className="p-5">
        <CardTitle className="flex flex-wrap">
          <p className="mr-auto text-lg font-bold">{Texts.shipping}</p>
          <div className="flex gap-x-3">
            {isAddressAdjustable && !isOrderAddressEditable ? (
              <Button
                onClick={() => setEditAddressOpen(true)}
                className="rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
              >
                {Texts.editAddress}
              </Button>
            ) : (
              <></>
            )}
            {isAddressAdjustable && isOrderAddressEditable && showActiveBrandSection ? (
              <Button
                onClick={() => setOpenEditAddressModal(true)}
                className="rounded-md border-[1px] border-foggyGray px-3 py-1.5 text-xs font-medium"
              >
                {Texts.editAddress}
              </Button>
            ) : (
              <></>
            )}
            {editAddressOpen && (
              <EditAddress
                Texts={Texts}
                open={editAddressOpen}
                setOpen={setEditAddressOpen}
                addresses={addresses}
                handleSubmitEditAddress={handleSubmitEditAddress}
                handleChangeAddress={handleChangeAddress}
                loading={loading}
                shippingAddress={shippingAddress}
                getUpdatedAddress={getUpdatedAddress}
                onOrderShippingAddressUpdated={handleOrderShippingAddressUpdated}
                customerId={customerId}
                handleAddressChange={handleAddressChange}
                selectedAddress={selectedAddress}
                setSelectedAddress={setSelectedAddress}
                handleSubmitAddress={handleSubmitAddress}
              />
            )}
            {openEditAddressModal && (
              <ShippingEditAddressModal
                open={openEditAddressModal}
                setOpen={setOpenEditAddressModal}
                data={shippingAddress}
                cartData={cartData}
                setShippingAddress={setShippingAddress}
                shippingAddress={shippingAddress}
              />
            )}

            <ConfirmEditAddressModal
              Texts={Texts}
              open={confirmEditOpen}
              setOpen={setConfirmEditOpen}
              updatedAddressPayload={updatedAddressPayload}
              getAddressUpdateStatus={getAddressUpdateStatus}
            ></ConfirmEditAddressModal>
          </div>

          {loading && <Loader />}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ul>
          <li className="gbmask mb-8 flex">
            <p className="w-1/3">{Texts.shippingAddress}:</p>
            <p className="w-2/3">
              {shippingAddress?.firstName} {shippingAddress?.lastName}
              <br />
              {shippingAddress?.streetName}
              <br />
              {shippingAddress?.additionalAddressInfo} {shippingAddress?.city}, {shippingAddress?.state}{' '}
              {maskPostalCode(shippingAddress?.postalCode ?? '')}
              <br />
              {maskPhoneNumber(shippingAddress?.phone ?? '')}
            </p>
          </li>
          <li className="mb-8 flex">
            <p className="w-1/3">{Texts.shippingMethods}:</p>
            <p className="w-2/3">
              {shippingDetails?.orderInfo?.shipViaDescription}
              <br />
            </p>
          </li>
          {giftMessage ? (
            <li className="mb-8 flex">
              <p className="w-1/3">{Texts.giftNote}:</p>
              <p className="gbmask w-2/3">
                {giftMessage}
                <br />
              </p>
            </li>
          ) : (
            <></>
          )}
        </ul>
      </CardContent>
    </Card>
  );
};

export default OrderShipmentDetails;
